import { FileText } from 'lucide-react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { Datatable, TableSettingsMenu } from '@@/datatables';
import {
  createPersistedStore,
  BasicTableSettings,
  FilteredColumnsTableSettings,
  RefreshableTableSettings,
  filteredColumnsSettings,
  refreshableSettings,
} from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { withColumnFilters } from '@@/datatables/extend-options/withColumnFilters';
import { mergeOptions } from '@@/datatables/extend-options/mergeOptions';
import { DeleteButton } from '@@/buttons/DeleteButton';
import { TableSettingsMenuAutoRefresh } from '@@/datatables/TableSettingsMenuAutoRefresh';

import { useDeleteCustomResourceDefinition } from '../queries/useDeleteCustomResourceDefinition';
import { useCustomResourceDefinitions } from '../queries/useCustomResourceDefinitions';

import { columns } from './columns';
import { CustomResourceDefinitionGroupFilter } from './CustomResourceDefinitionGroupFilter';

const storageKey = 'customResourceDefinitions';

interface TableSettings
  extends
    BasicTableSettings,
    FilteredColumnsTableSettings,
    RefreshableTableSettings {
  selectedGroup: string;
  setSelectedGroup: (group: string) => void;
}

const settingsStore = createPersistedStore<TableSettings>(
  storageKey,
  'name',
  (set) => ({
    ...filteredColumnsSettings(set),
    ...refreshableSettings(set),
    selectedGroup: '',
    setSelectedGroup: (selectedGroup: string) => set({ selectedGroup }),
  })
);

export function CustomResourceDefinitionsDatatable() {
  const environmentId = useEnvironmentId();
  const tableState = useTableState(settingsStore, storageKey);
  const { data: crds, isLoading } = useCustomResourceDefinitions(
    environmentId,
    {
      refetchInterval: tableState.autoRefreshRate
        ? tableState.autoRefreshRate * 1000
        : undefined,
      select: (data) =>
        tableState.selectedGroup
          ? data.filter((crd) => crd.group === tableState.selectedGroup)
          : data,
    }
  );
  const deleteMutation = useDeleteCustomResourceDefinition(environmentId);

  function handleRemove(selectedItems: { name: string }[]) {
    const definitionNames = selectedItems.map((item) => item.name);
    deleteMutation.mutate(definitionNames);
  }

  function handleGroupChange(group: string) {
    tableState.setSelectedGroup(group);
    // Set the column filter for the group column
    if (group) {
      tableState.setColumnFilters([{ id: 'group', value: group }]);
    } else {
      tableState.setColumnFilters([]);
    }
  }

  return (
    <Datatable
      dataset={crds || []}
      columns={columns}
      settingsManager={tableState}
      isLoading={isLoading}
      title="Custom Resource Definitions"
      titleIcon={FileText}
      getRowId={(row) => row.name}
      data-cy="crd-datatable"
      extendTableOptions={mergeOptions(
        withColumnFilters(tableState.columnFilters, tableState.setColumnFilters)
      )}
      renderTableActions={(selectedItems) => (
        <DeleteButton
          data-cy="crd-removeButton"
          disabled={selectedItems.length === 0 || deleteMutation.isLoading}
          isLoading={deleteMutation.isLoading}
          confirmMessage="Do you want to remove the selected custom resource definition(s)?"
          onConfirmed={() => handleRemove(selectedItems)}
        />
      )}
      renderTableSettings={() => (
        <TableSettingsMenu>
          <TableSettingsMenuAutoRefresh
            value={tableState.autoRefreshRate}
            onChange={tableState.setAutoRefreshRate}
          />
        </TableSettingsMenu>
      )}
      description={
        <div className="w-full">
          <div className="float-left min-w-[400px]">
            <CustomResourceDefinitionGroupFilter
              crds={crds || []}
              value={tableState.selectedGroup}
              onChange={handleGroupChange}
            />
          </div>
        </div>
      }
    />
  );
}
